/**
 * TurfPulseDashboard — the new home screen.
 *
 * Single-page receipt of "what's happening on the lawn right now" + "what
 * should I do." Data comes from a single `GET /api/dashboard/now` call.
 * No scrolling: 7 cards stacked tight, each clickable for deep-dive.
 *
 * Layout reference: 2026-05-21 mockup (WAIT/ACT hero + metric grid +
 * weather/water-load/windows row + active window slider + recommendation).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Clock, RefreshCw, Droplets, Leaf, Wind, Cloud, CloudRain,
  Lightbulb, Bell, Settings as SettingsIcon, BarChart3, Home,
  CheckCircle2, Ban, Activity, ShieldCheck, Sprout, Plus, ChevronRight,
} from 'lucide-react';
import { API_URL } from '../config/api';
import { useUnreadAlerts } from '../hooks/useUnreadAlerts';
import { AlertDot } from '../components/pulse/AlertDot';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { WaterNowSheet } from '../components/pulse/WaterNowSheet';

// Use the same relative-URL pattern every other authenticated page uses
// (config/api.js exports '' so /api/... stays same-origin). Cross-origin
// breaks the httpOnly access_token cookie → 401. Took one prod outage to
// learn — never hard-code REACT_APP_BACKEND_URL on auth'd pages.
const API = API_URL;

const ACTIVITY_COLOR = {
  LOW:      { ring: '#f59e0b', text: 'text-amber-500', bg: 'from-amber-950/40 to-emerald-950/30', icon: 'text-amber-500' },
  MODERATE: { ring: '#eab308', text: 'text-yellow-400', bg: 'from-yellow-950/40 to-emerald-950/30', icon: 'text-yellow-400' },
  HIGH:     { ring: '#22c55e', text: 'text-green-400', bg: 'from-emerald-950/50 to-green-950/30', icon: 'text-green-400' },
  PEAK:     { ring: '#10b981', text: 'text-emerald-400', bg: 'from-emerald-900/60 to-emerald-700/40', icon: 'text-emerald-400' },
};

const STATUS_BADGE = {
  OPEN:    { text: 'text-emerald-400', dot: 'bg-emerald-400' },
  CLOSED:  { text: 'text-rose-400',    dot: 'bg-rose-400' },
  UNKNOWN: { text: 'text-slate-500',   dot: 'bg-slate-500' },
};

/**
 * PulseLogoMark — the official animated TurfPulse mark (circle + EKG trace).
 *
 * Lives in the top-right corner of the mobile dashboard header (per user
 * request 2026-02-23: "TurfPulse logo Circle EKG" in the empty spot up top).
 * Mobile-only (md:hidden) — desktop layout stays as-is.
 *
 * Uses the approved `TurfPulseLogoSVG` from /components/TurfPulseLogo.js
 * (do NOT roll a custom EKG path — must match brand assets).
 */
function PulseLogoMark() {
  return (
    <div
      data-testid="dashboard-pulse-logo"
      className="md:hidden"
      aria-label="TurfPulse"
    >
      <TurfPulseLogoSVG animated size={40} />
    </div>
  );
}

function Card({ children, onClick, className = '', testid }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`bg-slate-900/80 border border-slate-800 rounded-2xl p-4 cursor-pointer hover:border-emerald-600/60 transition-colors ${className}`}
    >
      {children}
    </div>
  );
}

function ActivityRing({ score, band }) {
  const color = ACTIVITY_COLOR[band] || ACTIVITY_COLOR.LOW;
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative w-24 h-24" data-testid="dashboard-activity-ring">
      <svg width="96" height="96" className="-rotate-90">
        <circle cx="48" cy="48" r={radius} stroke="#1e293b" strokeWidth="6" fill="none" />
        <circle
          cx="48" cy="48" r={radius}
          stroke={color.ring} strokeWidth="6" fill="none" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">Turf<br/>Activity</div>
        <div className={`text-2xl font-bold ${color.text}`}>{score}%</div>
      </div>
    </div>
  );
}

function HeroCard({ activity }) {
  const color = ACTIVITY_COLOR[activity?.band] || ACTIVITY_COLOR.LOW;
  return (
    <Card
      testid="dashboard-hero-card"
      className={`bg-gradient-to-br ${color.bg} border-slate-800`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Clock className={`w-7 h-7 ${color.icon}`} />
            <span className={`text-3xl font-bold ${color.text}`} data-testid="dashboard-hero-headline">
              {activity?.headline || '—'}
            </span>
          </div>
          <p className="text-slate-200 text-sm font-medium leading-tight">
            {activity?.subhead || ''}
          </p>
          <p className="text-slate-400 text-xs mt-2 leading-snug">
            No watering, feeding, or spraying recommended right now.
          </p>
        </div>
        <ActivityRing score={activity?.score || 0} band={activity?.band || 'LOW'} />
      </div>
    </Card>
  );
}

function MetricTile({ icon: Icon, label, value, unit, statusLabel, valueColor, testid, emptyCta }) {
  const navigate = useNavigate();
  const isEmpty = value === null || value === undefined;
  // Empty-state CTA — first-time-user clarity. Instead of a silent "—"
  // (which reads as "broken"), surface the exact next step that unlocks
  // this metric (DESIGN_PRINCIPLES §47 spirit: be transparent about why
  // a number is missing, not just absent).
  if (isEmpty && emptyCta) {
    return (
      <Card
        testid={`${testid}-empty`}
        className="text-center border-dashed border-slate-700/80 hover:border-emerald-600/60"
        onClick={(e) => { e.stopPropagation(); navigate(emptyCta.href); }}
      >
        <div className="flex items-center gap-1.5 justify-center mb-2 text-xs uppercase tracking-wider text-slate-500">
          <Icon className="w-4 h-4" />
          {label}
        </div>
        <div className="text-2xl text-slate-600 font-light" data-testid={`${testid}-value`}>—</div>
        <div className="text-[10px] text-emerald-400 mt-1 font-semibold flex items-center justify-center gap-1">
          {emptyCta.label}<ChevronRight className="w-3 h-3" />
        </div>
      </Card>
    );
  }
  return (
    <Card testid={testid} className="text-center">
      <div className="flex items-center gap-1.5 justify-center mb-2 text-xs uppercase tracking-wider text-slate-400">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className={`text-3xl font-bold ${valueColor}`} data-testid={`${testid}-value`}>
        {isEmpty ? '—' : value}
        {unit ? <span className="text-sm font-normal ml-0.5">{unit}</span> : ''}
      </div>
      <div className="text-xs text-slate-400 mt-1">{statusLabel}</div>
    </Card>
  );
}

function WeatherCard({ weather }) {
  return (
    <Card testid="dashboard-weather-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Weather</span>
        <Cloud className="w-4 h-4 text-slate-400" />
      </div>
      <div className="space-y-1.5 text-sm">
        <div className="flex items-center gap-1.5"><span className="text-slate-300 w-12 font-mono">{weather?.temp_f != null ? `${weather.temp_f.toFixed(1)}°F` : '—'}</span><span className="text-slate-500 text-xs">Temp</span></div>
        <div className="flex items-center gap-1.5"><span className="text-slate-300 w-12 font-mono">{weather?.humidity_pct != null ? `${Math.round(weather.humidity_pct)}%` : '—'}</span><span className="text-slate-500 text-xs">Humidity</span></div>
        <div className="flex items-center gap-1.5"><span className="text-slate-300 w-12 font-mono">{weather?.dewpoint_f != null ? `${weather.dewpoint_f.toFixed(1)}°F` : '—'}</span><span className="text-slate-500 text-xs">Dew Pt</span></div>
        <div className="flex items-center gap-1.5"><span className="text-slate-300 w-12 font-mono">{weather?.wind_mph != null ? `${Math.round(weather.wind_mph)} mph` : '—'}</span><span className="text-slate-500 text-xs">{weather?.wind_dir || 'Wind'}</span></div>
      </div>
    </Card>
  );
}

function WaterLoadCard({ waterLoad }) {
  return (
    <Card testid="dashboard-water-load-card">
      <div className="flex items-center gap-1.5 mb-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">
        <CloudRain className="w-4 h-4 text-sky-400" />
        Water Load
      </div>
      <div className="text-[10px] uppercase text-slate-500">24h Rain</div>
      <div className="text-2xl font-bold text-sky-400" data-testid="dashboard-water-load-24h">{(waterLoad?.rain_24h_in ?? 0).toFixed(2)}"</div>
      <div className="border-t border-slate-800 my-2" />
      <div className="text-[10px] uppercase text-slate-500">Weekly Rain</div>
      <div className="text-2xl font-bold text-sky-400" data-testid="dashboard-water-load-weekly">{(waterLoad?.rain_weekly_in ?? 0).toFixed(2)}"</div>
      <div className="text-[11px] text-slate-400 mt-2 leading-tight">{waterLoad?.state || '—'}</div>
    </Card>
  );
}

function WindowsCard({ windows }) {
  const rows = [
    { key: 'spray', label: 'Spray' },
    { key: 'feed',  label: 'Feed'  },
    { key: 'water', label: 'Water' },
  ];
  return (
    <Card testid="dashboard-windows-card">
      <div className="flex items-center gap-1.5 mb-3 text-xs uppercase tracking-wider text-slate-400 font-semibold">
        <Ban className="w-4 h-4 text-rose-400" />
        Windows
      </div>
      <div className="space-y-2.5">
        {rows.map(r => {
          const status = windows?.[r.key]?.status || 'UNKNOWN';
          const sty = STATUS_BADGE[status] || STATUS_BADGE.UNKNOWN;
          return (
            <div key={r.key} className="flex items-center justify-between" data-testid={`dashboard-window-${r.key}`}>
              <span className="text-sm text-slate-200">{r.label}</span>
              <span className={`text-xs font-bold tracking-wider ${sty.text}`}>{status}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ActiveWindowCard({ active, metrics }) {
  if (!active) return null;
  const moisture = metrics?.moisture?.value;
  const target = 48; // configurable later
  const opens = 45;
  return (
    <Card testid="dashboard-active-window-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <Activity className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">{active.kind} Window</div>
            <div className="text-base font-bold text-emerald-400 leading-tight">{active.label}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-center">
          <div>
            <div className="text-[10px] text-slate-500 uppercase">Opens In</div>
            <div className="text-lg font-bold text-slate-100">~{active.opens_in_h}<span className="text-xs ml-0.5 text-slate-400">h</span></div>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase">Length</div>
            <div className="text-lg font-bold text-slate-100">~{active.closes_in_h}<span className="text-xs ml-0.5 text-slate-400">h</span></div>
          </div>
        </div>
      </div>
      {moisture != null && (
        <div className="mt-4">
          <div className="text-[10px] uppercase text-slate-500 mb-1">Moisture Trend</div>
          <div className="relative h-2 bg-slate-800 rounded-full">
            <div className="absolute inset-y-0 left-0 bg-amber-400 rounded-full" style={{ width: `${Math.min(100, moisture)}%` }} />
            <div className="absolute -top-1 w-3 h-3 bg-white rounded-full shadow" style={{ left: `calc(${Math.min(100, moisture)}% - 6px)` }} />
          </div>
          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
            <span><b className="text-slate-200">{moisture}%</b> Now</span>
            <span><b className="text-slate-200">{target}%</b> Target</span>
            <span><b className="text-slate-200">{opens}%</b> Opens</span>
          </div>
        </div>
      )}
    </Card>
  );
}

function RecommendationCard({ rec }) {
  if (!rec) return null;
  return (
    <Card testid="dashboard-recommendation-card" className="border-emerald-900/60">
      <div className="flex gap-4">
        <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
          <Lightbulb className="w-6 h-6 text-emerald-400" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Recommendation</div>
          <div className="text-2xl font-bold text-slate-100 leading-tight" data-testid="dashboard-rec-action">{rec.action}</div>
          <div className="text-xs text-slate-400 mt-1 leading-snug">{rec.subhead}</div>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-2">Next Action</div>
          <ul className="space-y-1.5">
            {(rec.next_actions || []).map((a, i) => (
              <li key={i} className="flex items-center gap-1.5 text-xs text-slate-300">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}


// PGR application band → visual theme. Bands come from
// /api/pgr/tracker (compute_pgr_progress in pgr_tracker.py).
const PGR_BAND_THEME = {
  fresh:         { ring: 'border-emerald-700/60', text: 'text-emerald-300', bar: 'bg-emerald-400', accent: 'text-emerald-400', dot: 'bg-emerald-400' },
  active:        { ring: 'border-emerald-700/60', text: 'text-emerald-300', bar: 'bg-emerald-400', accent: 'text-emerald-400', dot: 'bg-emerald-400' },
  reapply_soon:  { ring: 'border-amber-500/60',   text: 'text-amber-300',   bar: 'bg-amber-400',   accent: 'text-amber-400',   dot: 'bg-amber-400' },
  rebound_risk:  { ring: 'border-rose-500/60',    text: 'text-rose-300',    bar: 'bg-rose-400',    accent: 'text-rose-400',    dot: 'bg-rose-400 animate-pulse' },
};

/**
 * GDDCountdownCard — Feb 27 redesign per founder direction:
 * "We need to see the GDD to be accruing. What's displayed now can be moved
 * to the clickable page. While the count fills the card."
 *
 * The accruing GDD number is now the visual hero — the dashboard story is
 * "your bank is filling up toward the reapply target." Detail (product
 * name, days since, rate, etc.) lives on the /pgr page.
 *
 * Two states:
 *  • Empty: no PGR logged → CTA "Log your first PGR app" → /pgr
 *  • Logged: huge accruing GDD count, slim progress bar, "of X target" sub.
 *    Tap → /pgr for the full record.
 *
 * Source of truth: GET /api/pgr/tracker (unioned across pgr_applications
 * AND applications-where-product_type=PGR as of Feb 27 2026 fix).
 */
function GDDCountdownCard({ pgr, onRefresh, refreshing }) {
  if (!pgr) return null;

  // ── computed_at chip helper (shared by empty + active states) ───────
  const computedAtChip = (() => {
    if (!pgr.computed_at) return null;
    let label = '';
    try {
      const ms = Date.now() - new Date(pgr.computed_at).getTime();
      const sec = Math.max(0, Math.floor(ms / 1000));
      if (sec < 60) label = 'just now';
      else if (sec < 3600) label = `${Math.floor(sec / 60)}m ago`;
      else label = `${Math.floor(sec / 3600)}h ago`;
    } catch { label = ''; }
    return (
      <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-1.5">
        <Clock className="w-3 h-3" />
        <span>Updated {label}</span>
        <button
          type="button"
          data-testid="dashboard-gdd-countdown-refresh"
          disabled={refreshing}
          onClick={(e) => {
            e.stopPropagation();
            onRefresh?.();
          }}
          className="ml-1 inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="underline">Force refresh</span>
        </button>
      </div>
    );
  })();

  // ── Empty state ─────────────────────────────────────────────────────
  if (!pgr.has_application) {
    return (
      <Card testid="dashboard-gdd-countdown-empty" className="border-emerald-900/40 bg-emerald-950/20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <Sprout className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-400">GDD Countdown</div>
            <div className="text-base font-extrabold text-slate-100 leading-tight">Log your first PGR app</div>
            <div className="text-xs text-slate-400 leading-snug mt-0.5">
              Track Primo/Anuew once and we'll count GDD to your next application.
            </div>
            {computedAtChip}
          </div>
          <div className="flex items-center gap-1 text-emerald-400 flex-shrink-0">
            <Plus className="w-4 h-4" />
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </Card>
    );
  }

  // ── Active state — accruing GDD fills the card ──────────────────────
  const progress = pgr.progress || {};
  const theme = PGR_BAND_THEME[progress.band] || PGR_BAND_THEME.active;
  const gddAccrued = Math.round(progress.gdd_since_application || 0);
  const gddTarget = Math.round(progress.gdd_target || 0);
  const pct = Math.min(100, Math.round(progress.gdd_progress_pct || 0));
  const isRebound = progress.band === 'rebound_risk';

  return (
    <Card testid="dashboard-gdd-countdown-card" className={`${theme.ring}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
          <Sprout className={`w-6 h-6 ${theme.accent}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[10px] uppercase tracking-wider font-bold ${theme.text}`}>
              GDD Countdown · accruing
            </span>
            {isRebound && (
              <span className="text-[10px] uppercase tracking-wider font-bold text-rose-300 bg-rose-500/15 border border-rose-500/40 rounded-full px-2 py-0.5">
                Due
              </span>
            )}
          </div>
          {/* HERO — the accruing number fills the card */}
          <div className="mt-1 flex items-baseline gap-2">
            <span
              data-testid="dashboard-gdd-countdown-value"
              className={`text-5xl font-extrabold leading-none ${theme.accent}`}
            >
              {gddAccrued}
            </span>
            <span className={`text-base font-bold ${theme.text}`}>GDD</span>
            <span className="text-xs text-slate-400 ml-auto">
              of {gddTarget} target
            </span>
          </div>
          <div className="mt-2.5 h-2 rounded-full bg-slate-800 overflow-hidden">
            <div
              className={`h-full ${theme.bar} transition-all duration-700`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-1 text-[10px] text-slate-400 text-right">
            <b className={theme.text}>{pct}%</b> to next app
          </div>
          {computedAtChip}
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0 mt-1" />
      </div>
    </Card>
  );
}



const STATUS_THEME = {
  SATURATED_HOLD:  { ring: 'border-indigo-500/60',  bg: 'bg-indigo-500/10',  text: 'text-indigo-300',  iconBg: 'bg-indigo-500/20',  iconClr: 'text-indigo-300',  Icon: Droplets,       pulse: true  },
  WET_LOCK:        { ring: 'border-sky-500/60',     bg: 'bg-sky-500/10',     text: 'text-sky-300',     iconBg: 'bg-sky-500/20',     iconClr: 'text-sky-300',     Icon: Ban,            pulse: true  },
  GOVERNOR_LOCK:   { ring: 'border-amber-500/60',   bg: 'bg-amber-500/10',   text: 'text-amber-300',   iconBg: 'bg-amber-500/20',   iconClr: 'text-amber-300',   Icon: Ban,            pulse: true  },
  RAIN_SKIP:       { ring: 'border-blue-500/60',    bg: 'bg-blue-500/10',    text: 'text-blue-300',    iconBg: 'bg-blue-500/20',    iconClr: 'text-blue-300',    Icon: CloudRain,      pulse: false },
  DRAIN_MODE:      { ring: 'border-cyan-500/60',    bg: 'bg-cyan-500/10',    text: 'text-cyan-300',    iconBg: 'bg-cyan-500/20',    iconClr: 'text-cyan-300',    Icon: Droplets,       pulse: true  },
  RECOVERY_WAIT:   { ring: 'border-orange-500/60',  bg: 'bg-orange-500/10',  text: 'text-orange-300',  iconBg: 'bg-orange-500/20',  iconClr: 'text-orange-300',  Icon: Wind,           pulse: true  },
  HIGH_TEMP_HOLD:  { ring: 'border-rose-500/60',    bg: 'bg-rose-500/10',    text: 'text-rose-300',    iconBg: 'bg-rose-500/20',    iconClr: 'text-rose-300',    Icon: Wind,           pulse: true  },
  READY:           { ring: 'border-emerald-700/60', bg: 'bg-emerald-500/10', text: 'text-emerald-300', iconBg: 'bg-emerald-500/20', iconClr: 'text-emerald-300', Icon: ShieldCheck,    pulse: false },
};

function LiveStatusCard({ status }) {
  if (!status) return null;
  const theme = STATUS_THEME[status.state] || STATUS_THEME.READY;
  const Icon = theme.Icon;
  return (
    <Card
      testid="dashboard-live-status-card"
      className={`${theme.bg} ${theme.ring}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-full ${theme.iconBg} flex items-center justify-center flex-shrink-0 ${theme.pulse ? 'animate-pulse' : ''}`}>
          <Icon className={`w-6 h-6 ${theme.iconClr}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-wider font-bold ${theme.text}`}>
              Engine Status
            </span>
            {status.rule_id != null && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 font-mono">
                RULE {status.rule_id}
              </span>
            )}
          </div>
          <div
            data-testid="dashboard-live-status-label"
            className={`text-lg font-extrabold leading-tight ${theme.text} mt-0.5`}
          >
            {status.label}
          </div>
          <div className="text-xs text-slate-300 mt-0.5 leading-snug">{status.headline}</div>
          {status.subhead && (
            <div className="text-[11px] text-slate-400 mt-1 leading-snug">{status.subhead}</div>
          )}
          {status.next_action && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-300">
              <CheckCircle2 className={`w-3.5 h-3.5 ${theme.iconClr} flex-shrink-0`} />
              <span>{status.next_action}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}


export default function TurfPulseDashboard() {
  const [data, setData] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [pgr, setPgr] = useState(null);
  const [pgrRefreshing, setPgrRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { total: unreadTotal, high: unreadHigh } = useUnreadAlerts();

  // Open the "Water Now" sheet when arriving from a push deep link:
  //   /pulse?run_zone=zone-2&min=6
  const runZoneId = searchParams.get('run_zone');
  const runZoneMin = Number(searchParams.get('min') || 6);
  const closeRunSheet = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('run_zone');
    next.delete('min');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TurfPulse uses cookie-based session auth — match the rest of the
      // app (axios + withCredentials). Sending a bogus Authorization header
      // was causing prod 401s when localStorage didn't have a 'token' key.
      const [dashRes, statusRes, pgrRes] = await Promise.all([
        fetch(`${API}/api/dashboard/now`, { credentials: 'include' }),
        fetch(`${API}/api/moisture-alerts/live-status`, { credentials: 'include' }),
        fetch(`${API}/api/pgr/tracker`, { credentials: 'include' }),
      ]);
      if (!dashRes.ok) throw new Error(`HTTP ${dashRes.status}`);
      const j = await dashRes.json();
      setData(j);
      if (statusRes.ok) {
        const s = await statusRes.json();
        setLiveStatus(s);
      } else {
        // Live status is non-critical — don't fail the whole dashboard.
        setLiveStatus(null);
      }
      if (pgrRes.ok) {
        const p = await pgrRes.json();
        setPgr(p);
      } else {
        // PGR tracker is non-critical too — empty-state card still renders
        // with a "log your first" CTA if we set has_application=false.
        setPgr({ has_application: false });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Force-refresh just the PGR tracker (lets the user verify the GDD math
  // is now live without F5-spamming the whole dashboard).
  const refreshPgr = useCallback(async () => {
    setPgrRefreshing(true);
    try {
      const r = await fetch(`${API}/api/pgr/tracker`, { credentials: 'include' });
      if (r.ok) {
        const p = await r.json();
        setPgr(p);
      }
    } catch {
      // Silent — keep showing whatever was last successful
    } finally {
      setPgrRefreshing(false);
    }
  }, []);

  const ageMin = data?.live_data_age_seconds != null
    ? Math.floor(data.live_data_age_seconds / 60) : null;
  const ageLabel = ageMin == null ? 'never' : ageMin < 1 ? 'just now' : `${ageMin}m ago`;

  return (
    <div
      className="min-h-screen bg-black text-slate-100"
      style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
    >
      <div className="max-w-md mx-auto px-4 pt-4 space-y-3">
        {/* Header — original lockup restored Feb 27 2026: animated circle+EKG
            PulseLogoMark on the left + "TurfPulse" wordmark, refresh on right.
            Replaces the brain-pulse PNG that the founder didn't want. */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2" data-testid="dashboard-brand-mark">
            <TurfPulseLogoSVG animated size={36} />
            <span className="text-xl font-extrabold tracking-tight text-slate-100">
              TurfPulse
            </span>
          </div>
          <button
            onClick={fetchData}
            data-testid="dashboard-refresh-btn"
            className="p-2 rounded-full hover:bg-slate-800 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-slate-400">
            <b className="text-slate-200">Live Data</b> Updated {ageLabel}
          </span>
        </div>

        {error && (
          <div data-testid="dashboard-error" className="bg-rose-950/40 border border-rose-900 rounded-xl p-3 text-xs text-rose-300">
            Failed to load: {error}
          </div>
        )}

        {loading && !data ? (
          <div className="text-center text-slate-400 py-10">Loading dashboard…</div>
        ) : data && (
          <>
            {/* Each card click navigates to its deep-dive page. */}
            <div onClick={() => navigate('/scorecard')}>
              <HeroCard activity={data.activity} />
            </div>

            {/* Live Engine Status — first thing the user sees: WET LOCK,
                SATURATED HOLD, RAIN SKIP, READY, etc. Founder Feb 24:
                "I want to see what phase the system is in." Tap to jump
                to the recent-fires timeline where each state is explained
                with actual rule triggers + suppressions for the last 7 days. */}
            {liveStatus && (
              <div
                onClick={() => navigate('/fires')}
                data-testid="dashboard-live-status-link"
              >
                <LiveStatusCard status={liveStatus} />
              </div>
            )}

            {/* GDD Countdown — founder Feb 25: "we needed a spot for our
                GDD countdown, clickable to add our PGR info." Empty-state
                CTA when no PGR logged; full progress bar + days-since
                when active. Whole card is the tap target → /pgr. */}
            {pgr && (
              <div
                onClick={() => navigate('/pgr')}
                data-testid="dashboard-gdd-countdown-link"
              >
                <GDDCountdownCard
                  pgr={pgr}
                  onRefresh={refreshPgr}
                  refreshing={pgrRefreshing}
                />
              </div>
            )}

            {/* Metrics row — each tile drills into its own Pulse detail page */}
            <div className="grid grid-cols-3 gap-3">
              <div onClick={() => navigate('/pulse/moisture')}>
                <MetricTile
                  testid="dashboard-metric-moisture"
                  icon={Droplets} label="Moisture"
                  value={data.metrics.moisture.value}
                  unit={data.metrics.moisture.unit}
                  statusLabel={data.metrics.moisture.label}
                  valueColor="text-sky-400"
                  emptyCta={{ label: 'Connect Ecowitt probes', href: '/sensors/placement' }}
                />
              </div>
              <div onClick={() => navigate('/pulse/ec')}>
                <MetricTile
                  testid="dashboard-metric-ec"
                  icon={Leaf} label="EC (Soil)"
                  value={data.metrics.ec.value}
                  unit=""
                  statusLabel={data.metrics.ec.label}
                  valueColor="text-emerald-400"
                  emptyCta={{ label: 'Add a TH/EC probe', href: '/sensors/placement' }}
                />
              </div>
              <div onClick={() => navigate('/pulse/vpd')}>
                <MetricTile
                  testid="dashboard-metric-vpd"
                  icon={Wind} label="VPD"
                  value={data.metrics.vpd.value != null ? data.metrics.vpd.value.toFixed(2) : null}
                  unit=""
                  statusLabel={data.metrics.vpd.label}
                  valueColor="text-purple-400"
                />
              </div>
            </div>

            {/* Info row: weather / water load / windows — each drills */}
            <div className="grid grid-cols-3 gap-3">
              <div onClick={() => navigate('/scorecard')}>
                <WeatherCard weather={data.weather} />
              </div>
              <div onClick={() => navigate('/pulse/moisture')}>
                <WaterLoadCard waterLoad={data.water_load} />
              </div>
              <div onClick={() => navigate('/pulse/windows')}>
                <WindowsCard windows={data.windows} />
              </div>
            </div>

            {/* Active window → mow window detail (where they tap to learn timing) */}
            <div onClick={() => navigate('/pulse/mow-window')}>
              <ActiveWindowCard active={data.windows?.active} metrics={data.metrics} />
            </div>

            {/* Recommendation → scorecard (the engine receipt) */}
            <div onClick={() => navigate('/scorecard')}>
              <RecommendationCard rec={data.recommendation} />
            </div>
          </>
        )}
      </div>

      {/* Bottom nav — left-aligned, leaves a ~180px gap on the right for
          the Emergent platform watermark on hosted deploys. Without this
          shift, Alerts + Settings sit BEHIND the "Made with Emergent" pill.
          gap-1 + py-1.5 give each icon a comfortable tap target without
          the icons visually overlapping their labels on small phones. */}
      <div
        className="fixed bottom-0 inset-x-0 bg-slate-950/95 backdrop-blur border-t border-slate-800 z-30"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="w-full px-4 py-1.5 grid grid-cols-5 text-center text-[10px] text-slate-400 gap-1">
          <button data-testid="nav-home"     className="flex flex-col items-center gap-1 py-1.5 text-emerald-400"><Home className="w-5 h-5"/><span>Home</span></button>
          <button data-testid="nav-history"  onClick={() => navigate('/activity')}    className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><Clock className="w-5 h-5"/><span>History</span></button>
          <button data-testid="nav-trends"   onClick={() => navigate('/trends')}      className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><BarChart3 className="w-5 h-5"/><span>Trends</span></button>
          <button data-testid="nav-alerts"   onClick={() => navigate('/alerts')}      className="relative flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><span className="relative"><Bell className="w-5 h-5"/><AlertDot total={unreadTotal} high={unreadHigh} /></span><span>Alerts</span></button>
          <button data-testid="nav-settings" onClick={() => navigate('/profile')}     className="flex flex-col items-center gap-1 py-1.5 hover:text-slate-200"><SettingsIcon className="w-5 h-5"/><span>Settings</span></button>
        </div>
      </div>

      {/* Water Now sheet — opened by /pulse?run_zone=...&min=... deep links
          (from push notifications). */}
      {runZoneId && (
        <WaterNowSheet
          zoneId={runZoneId}
          minutes={runZoneMin}
          onClose={closeRunSheet}
        />
      )}
    </div>
  );
}
